import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Here's why you should never implement your own caching",
  "description": "",
  "published": "2017-04-13T00:00:00.000Z",
  "redirect_from": ["/blog/heres-why-you-should-never-implement-your-own-caching/swizec/7567"],
  "categories": "Technical",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <lite-youtube {...{
      "videoid": "PU8kGUrC1dw?ecver=2",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Here's an interesting problem for you: build some simple caching.`}</p>
    <p>{`Let's say you have a server and an app. Your server has to do something every time you release a new app. Asking for reviews is a good example`}<a parentName="p" {...{
        "href": "#fn1"
      }}>{`1`}</a>{`. If you automate this, everyone will be happier.`}</p>
    <p>{`So you keep track of the latest app version and run checks when users ping your API. The list of versions is going to grow fast-ish, and your `}<inlineCode parentName="p">{`SELECT`}</inlineCode>{` statement has to run in code because Postgres doesn't know how to compare version strings.`}</p>
    <p>{`In Ruby, finding the latest app version looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`AppVersion.where(platform: platform)
          .sort_by{ |v| Gem::Version.new(v.version) }
          .last
`}</code></pre>
    <p>{`This looks innocent, but it builds an array of all `}<inlineCode parentName="p">{`AppVersion`}</inlineCode>{` models, then sorts it in Ruby, then takes the last one and discards the rest. It's kind of okay when the table is small, but it’s `}<em parentName="p">{`terrible`}</em>{` when the table grows big.`}</p>
    <p>{`Ruby on Rails's and your database's default caching strategies can't cache this call. You have to run it every time.`}</p>
    <p>{`The result only changes every few days. Sometimes, it goes unchanged for weeks. But you still have to check every time a user logs in because maybe they're the first person with a new app version, and you need to know when it showed up.`}</p>
    <p>{`?`}</p>
    <h2 {...{
      "id": "a-naive-caching-strategy"
    }}>{`A naive caching strategy`}</h2>
    <p>{`You should use caching, obviously. Somewhat expensive thing to calculate that is checked often and changes rarely. Cache!`}</p>
    <p>{`So you implement the simplest approach: memoization.`}</p>
    <blockquote>
      <p parentName="blockquote">{`In computing, memoization or memoisation is an optimization technique used primarily to speed up computer programs by storing the results of expensive function calls and returning the cached result when the same inputs occur again.`}</p>
    </blockquote>
    <p>{`Your code looks something like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`class AppVersion < ActiveRecord::Base

  @current = {}

  def self.latest(platform)
    if @current[platform].nil?
      @current[platform] = where(platform: platform)
         .sort_by{ |v| Gem::Version.new(v.version) }
         .last
    end

    @current[platform]
  end

  def self.check_app_version(device)
    latest = self.latest(device.platform)

    if latest
      if Gem::Version.new(device.app_version) > Gem::Version.new(latest.version)
        latest = new_latest(device)
        @current[device.platform] = latest
      else
        AppVersion.find_or_create_by(version: device.app_version,
                                     platform: device.platform)
          .update!(last_seen_at: DateTime.now)
      end
    else
      latest = new_latest(device)
      @current[device.platform] = latest
    end

    latest
  end
`}</code></pre>
    <p>{`?`}</p>
    <p>{`Overall, this is the `}<inlineCode parentName="p">{`AppVersion`}</inlineCode>{` model. It stores information about each new app version the server encounters. When a user logs in, we call `}<inlineCode parentName="p">{`AppVersion.check_app_version(user.device)`}</inlineCode>{`.`}</p>
    <p>{`This function:`}</p>
    <ol>
      <li parentName="ol">{`Fetches the `}<inlineCode parentName="li">{`latest`}</inlineCode>{` app version`}</li>
      <li parentName="ol">{`1.1. `}{`\``}{`latest`}{`\``}{` returns saved value if it exists`}</li>
      <li parentName="ol">{`1.2. if not, `}{`\``}{`latest`}{`\``}{` saves its result in a class instance variable`}</li>
      <li parentName="ol">{`If we got a version, we check if the new app is of a newer version`}</li>
      <li parentName="ol">{`If user's device is newer, we create a new `}<inlineCode parentName="li">{`AppVersion`}</inlineCode>{` entry`}</li>
      <li parentName="ol">{`Then we update the class instance variable`}</li>
      <li parentName="ol">{`If device is not newer, we update the `}<inlineCode parentName="li">{`last_seen_at`}</inlineCode>{` timestamp`}</li>
      <li parentName="ol">{`If this is the first time ever that we're checking – there's no `}<inlineCode parentName="li">{`latest`}</inlineCode>{` – then we make a new latest and update the class instance variable`}</li>
    </ol>
    <p>{`Seems reasonable, right? Calculate value, save value, update value when needed. We rely on class instance variables persisting across requests.`}</p>
    <p>{`You do some testing locally, you write some tests. All good. Feature works. Ship it.`}</p>
    <p>{`Feature goes to production, you release a new app, it creates 18 entries in the database ?`}</p>
    <p>{`What went wrong?`}</p>
    <h2 {...{
      "id": "the-correct-caching-strategy"
    }}>{`The correct caching strategy`}</h2>
    <p>{`The clue is in that `}<inlineCode parentName="p">{`18`}</inlineCode>{` number. Our code thought exactly 18 times that it had encountered a new latest app version.`}</p>
    <p>{`At the time, we had `}<inlineCode parentName="p">{`9`}</inlineCode>{` Heroku dynos with `}<inlineCode parentName="p">{`4`}</inlineCode>{` threads each running in production. `}<inlineCode parentName="p">{`9*4 = 36`}</inlineCode>{`, which is not 18. `}<em parentName="p">{`But`}</em>{` it's twice as much as 18 and class instance variables are meant to be shared between threads.`}</p>
    <p>{`Perhaps the way Puma shares memory between threads, or potential race conditions in our code, means that it took 2 tries before every thread on a machine knew about the new version. It's hard to say `}<em parentName="p">{`why`}</em>{` it works out that way, but it does.`}</p>
    <p>{`Memoization does not work. Caching is hard.`}</p>
    <p>{`In retrospect, it is obvious that this was never going to work. Our "server" is distributed among multiple virtual machines. They don't share memory. How would they ever have seen each other's class instance variables?`}</p>
    <p>{`The answer is to stop trying to be clever. Rails has built-in caching that's been battle tested and developed by smart people.`}</p>
    <p>{`Fixed code looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-ruby"
      }}>{`def self.latest(platform)
    Rails.cache.fetch("latest_app_version/#{platform}") do
      where(platform: platform).sort_by{ |v| Gem::Version.new(v.version) }.last
    end
  end

  def self.check_app_version(device)
    latest = self.latest(device.platform)

    if latest
      if Gem::Version.new(device.app_version) > Gem::Version.new(latest.version)
        latest = new_latest(device)
        Rails.cache.delete("latest_app_version/#{device.platform}")
      else
        AppVersion.find_or_create_by(version: device.app_version,
                                     platform: device.platform)
          .update!(last_seen_at: DateTime.now)
      end
    else
      latest = new_latest(device)
    end

    latest
  end
`}</code></pre>
    <p>{`Much better! Logic is same as before, except that we use `}<inlineCode parentName="p">{`Rails.cache`}</inlineCode>{` as our caching mechanism.`}</p>
    <p><inlineCode parentName="p">{`.fetch`}</inlineCode>{` reads from cache and if there's a miss, it runs the provided block and stores its result. `}<inlineCode parentName="p">{`.delete`}</inlineCode>{` deletes the cached value so next time we use `}<inlineCode parentName="p">{`latest`}</inlineCode>{`, it reads from cache.`}</p>
    <p>{`The fixed code works because `}<inlineCode parentName="p">{`Rails.cache`}</inlineCode>{` can be configured to use an external caching server – Memcache or Redis for instance. This creates a memory space shared between all server machines and server threads.`}</p>
    <p>{`Problem solved, crisis averted, lesson learned. Don't be clever. Use the tools your frameworks give you. ?`}</p>
    <ol>
      <li parentName="ol">{`Apple promotes reviews for the latest app version. It hides previous reviews. If you have frequent updates, like you should, and you have good reviews, like you hope, then all your expensive traffic sees `}<em parentName="li">{`"This app has not received enough ratings to show an average"`}</em>{`. How encouraging! (By the way, you pay for traffic because this is the real world and if you build it they will come does not work.) `}<a parentName="li" {...{
          "href": "#ffn1"
        }}>{`↩`}</a></li>
    </ol>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      